import { createUseCase } from '@brickflow/http'

import type { StorageService } from '@/core/services'

import { Query } from '@/core/constants/route'
import { createGet } from '@/core/util'

import type useRoot from '../root/use-case'
import type { User, UserAuth, UserPromocode } from './entity/base'

const useUser = createUseCase<{
  localeInst: ReturnType<typeof useLocale>
  notify: ReturnType<typeof useNotify>
  root: ReturnType<typeof useRoot>
  route: ReturnType<typeof useRoute>
  router: ReturnType<typeof useRouter>
  storageService: StorageService
  userStore: ReturnType<typeof useBrickUserStore>
}>()(({ httpClient, localeInst, notify, root, route, router, storageService, userStore }) => {
  const transport = {
    async addEmail(email: string) {
      const result = await httpClient.post(
        'login/add_email',
        { email },
        {
          ignore(response) {
            return (
              response.data?.error_message === 'try_later' ||
              response.data?.error_message === 'email_already_used' ||
              response.data?.error_message === 'internal_error' ||
              response.data?.error_message === 'wrong_params'
            )
          },
        },
      )
      return result.data
    },
    async addPassword(data: { message: string; passwordData: { password: string } }) {
      const result = await httpClient.post('login/add_password', data.passwordData, {
        ignore(response) {
          return response.data?.error_message === 'try_later' || response.data?.error_message === 'internal_error'
        },
      })
      return result.data
    },
    async addPhone(phone: string) {
      const result = await httpClient.post(
        'login/add_phone',
        { phone },
        {
          ignore(response) {
            return (
              response.data?.error_message === 'try_later' ||
              response.data?.error_message === 'email_already_used' ||
              response.data?.error_message === 'internal_error' ||
              response.data?.error_message === 'wrong_params'
            )
          },
        },
      )
      return result.data
    },
    async changePassword(data: { old_password: string; password: string }) {
      const result = await httpClient.post('login/change_password', data)
      return result.data
    },
    async confirmPhoneCode(payload: { code: string; phone: string }) {
      const result = await httpClient.post('phone_confirm/confirm_code', payload)
      return result.data
    },
    async fastRegister() {
      const result = await httpClient.post(`login/fast`, { cache: false })
      return result.data
    },
    async generateReferralPromocode(code: string) {
      const result = await httpClient.post('user/referral_promocode/generate_code', { code })
      return result.data
    },
    getActiveLockeds: createGet()('user/active_locked'),
    async getAffilateTransactions(data: { limit: number; offset: number }, params?: { code: string }) {
      const result = await httpClient.post(`partner/transactions`, data, { params })
      return result.data
    },
    async getAffiliate(query?: { code: string }) {
      const result = await httpClient.get('partner/index', query ? { params: query } : undefined)
      return result.data
    },
    async getBanners() {
      const result = await httpClient.get(`profile_media/get_banners`)
      return result.data
    },
    async getDepositPendingTransactions() {
      const result = await httpClient.get(`user/transactions/deposit/pending`)
      return result.data
    },
    async getDepositTransactions(query: Record<string, number | string>) {
      const result = await httpClient.get(`user/deposit_transactions/`, { params: query })
      return result.data
    },
    async getInventory(
      payload: {
        game?: string
        item_type?: string
        limit: number
        name?: string
        offset: number
        price_from?: null | number
        price_to?: null | number
        rarity?: string
        sort_direction?: string
        sort_type?: string
        status?: string
        user_id?: null | number
      },
      signal?: AbortSignal,
    ) {
      const params = {
        ...payload,
        price_from: payload.price_from ?? undefined,
        price_to: payload.price_to ?? undefined,
        user_id: payload.user_id ?? undefined,
      }

      const result = await httpClient.get('inventory/user_inventory', {
        params,
        signal,
      })
      return result.data
    },
    async getPayoutsHistory(query: Record<string, number | string>) {
      const result = await httpClient.get(`user/payout_history/`, { params: query })
      return result.data
    },
    async getPhoneCode(payload: { hash: string; id: string; phone: string }) {
      const result = await httpClient.post('phone_confirm/send_code', payload)
      return result.data
    },
    async getProfileStats() {
      const result = await httpClient.get('user/profile')
      return result.data
    },
    async getProvablyFair() {
      const result = await httpClient.get('user/get_provably_fair_data')
      return result.data
    },
    async getProvablyFairSeedPairs() {
      const result = await httpClient.get('user/provably_fair_seed_pairs')
      return result.data
    },
    async getProvablyGameList(gameName: string) {
      const result = await httpClient.get(`v2/${gameName}/history`, {
        params: {
          limit: '20',
          only_my_games: 'true',
        },
      })
      return result.data
    },
    getReferalInfo: createGet()('user/referral/link'),
    async getTokens() {
      const result = await httpClient.get('user/tokens')
      return result.data
    },
    async getUserBonusHistory() {
      const result = await httpClient.get('user/bonus_spin_history')
      return result.data
    },
    async getUserGameHistory(
      userId: number,
      game: null | string,
      query: Record<string, number | string | undefined>,
    ) {
      const result = await httpClient.get(`v2/profile/games_history/${userId}/${game}/`, {
        params: query,
      })
      return result.data
    },
    async getUserProfileInfo(userId: number) {
      const result = await httpClient.get(`user/profile/${userId}`)
      return result.data
    },
    async getUserTopDrop(query?: { limit: number; user_id: null | number }) {
      const result = await httpClient.get(
        'inventory/user_top_drop',
        query
          ? {
              params: {
                ...query,
                user_id: query.user_id ?? undefined,
              },
            }
          : undefined,
      )
      return result.data
    },
    async getUserTransactions(query: Record<string, number | string | string[]>, signal?: AbortSignal) {
      const result = await httpClient.get(`user/user_transactions`, {
        params: query,
        signal,
      })
      return result.data
    },
    getUserWithdrawLimits: createGet()('user/transactions/payout/count_limits'),
    async getVipAvatars() {
      const result = await httpClient.get(`profile_media/get_avatars`)
      return result.data
    },
    async login(data: {
      login: string
      password: string
      powSolution?: number
      socialConfirmToken?: null | string
    }) {
      const form = new FormData()
      if (data.socialConfirmToken) {
        form.append('auto_link_social_confirm_token', data.socialConfirmToken)
      }
      form.append('password', data.password)
      form.append('remember', '1')
      if (data.powSolution) {
        form.append('pow_solution', String(data.powSolution))
      }
      form.append('username', data.login)
      const result = await httpClient.post('login/authorize', form)
      return result.data
    },
    async loginTelegram(data: { uri_hash: string }) {
      const form = new FormData()
      form.append('uri_hash', data.uri_hash)

      const result = await httpClient.post('login/telegram_app_auth/callback', form)

      return result.data
    },
    async loginWithPhone(data: {
      login: string
      password: string
      socialConfirmToken?: null | string
    }): Promise<UserAuth> {
      const form = new FormData()
      form.append('password', data.password)
      form.append('remember', '1')
      form.append('username', data.login)
      const result = await httpClient.post('login/phone/signin', form, {
        ignore(response) {
          return response.data?.error_message === 'phone_confirm_required'
        },
      })
      return result.data
    },
    async logout() {
      await httpClient.post('login/logout')
    },
    async platformPreferred() {
      await httpClient.post(
        'user/set_platform_preferred',
        {},
        {
          ignore(response) {
            return response.data?.error_message === 'wrong_params'
          },
        },
      )
    },
    async recovery(username: string) {
      const form = new FormData()
      form.append('username', username)
      const result = await httpClient.post('login/lost_password', form)
      return result.data
    },
    async register(data: { login: string; password: string }): Promise<UserAuth> {
      const form = new FormData()
      form.append('password', data.password)
      form.append('remember', '1')
      form.append('username', data.login)
      const result = await httpClient.post('login/signup', form, {
        ignore(response) {
          return response.data?.error_message === 'phone_confirm_required'
        },
      })
      return result.data
    },
    async registerLike(userId: number) {
      const result = await httpClient.post(`v2/profile/reaction/${userId}/like`)
      return result.data
    },
    async registerWithPhone(data: { email: string; login: string; password: string }): Promise<UserAuth> {
      const form = new FormData()
      form.append('password', data.password)
      form.append('remember', '1')
      form.append('username', data.login)
      form.append('email', data.email)
      const result = await httpClient.post('login/phone/signup', form, {
        ignore(response) {
          return (
            response.data?.error_message === 'phone_confirm_required' ||
            response.data?.error_message === 'phone_already_used' ||
            response.data?.error === 'phone_already_used'
          )
        },
      })

      if (result.status === 503) {
        showError({
          status: 503,
        })

        throw new Error('Error')
      }

      return result.data
    },
    async resendEmail() {
      const result = await httpClient.post(
        'login/email_confirm_resend',
        {},
        {
          ignore(response) {
            return (
              response.data?.error_message === 'try_later' ||
              response.data?.error_message === 'email_already_used' ||
              response.data?.error_message === 'internal_error'
            )
          },
        },
      )

      return result.data
    },
    async revealServerSeed() {
      const result = await httpClient.post('user/reveal_server_seed')
      return result.data
    },
    async saveOneSignalId(payload: {
      authToken?: string
      oneSignalId: string
      oneSignalSubscriptionId?: string
      platform?: 'app' | 'web'
    }) {
      const result = await httpClient.post(
        'onesignal/save_id',
        {
          one_signal_id: payload.oneSignalId,
          platform: payload.platform ?? 'web',
          ...(payload.oneSignalSubscriptionId && { one_signal_subscription_id: payload.oneSignalSubscriptionId }),
        },
        {
          ignore: () => true,
          params: payload.authToken ? { auth_token: payload.authToken } : undefined,
        },
      )
      return result.data
    },
    async sellInventorySkins(payload: { ids?: number[]; isAll?: boolean }) {
      const result = await httpClient.post('inventory/convert_balance', {
        inventory_ids: payload.ids || [],
        is_all: Number(payload.isAll),
      })
      return result.data
    },
    async sendMarketingTrigger(trigger: string) {
      await httpClient.post(
        'v2/marketing_flow/trigger',
        { trigger },
        {
          ignore: () => true,
        },
      )
    },
    async sendVerification(code: string) {
      const form = new FormData()
      form.append('code', code)
      return await httpClient.post('login/second_factor_verification', form)
    },
    async setAppInstalled(type: 'pwa' | 'twa') {
      await httpClient.post('user/set_app_installed', { type })
    },
    async setCurrentCurrencyByCode(code: string) {
      await httpClient.post(
        'user/set_currency',
        { currency: code },
        {
          ignore(response) {
            return response.data?.error_message === 'need_auth'
          },
        },
      )
    },
    async setDisableFactorAuth(code: string) {
      return await httpClient.post('user/disable_2fa', { code })
    },
    async setGaData(payload: { client_id: string; session_id: string }) {
      try {
        await httpClient.post('user/set_ga_data', payload)
      } catch (error) {
        console.info(error)
      }
    },
    async setLocale(lang: string) {
      return await httpClient.post('user/set_preferred_locale', { preferred_locale: lang })
    },
    async setNewUserName(name: string) {
      const response = await httpClient.post('user/update_nickname', { nickname: name })
      return response.data
    },
    async setPaymentUnlockedRegion() {
      const result = await httpClient.post('user/set_payment_unlocked_region')
      return result.data
    },
    async setProfileVisibilityStatus(status: string) {
      return await httpClient.post('user/set_profile_visibility', {
        visibility: status,
      })
    },
    async setPromocode(code: string) {
      const response = await httpClient.post(
        'user/promocode',
        { code },
        {
          ignore(res) {
            return res.data?.status === 'error'
          },
        },
      )
      return response.data
    },
    async setRejectPayout(id: number) {
      const response = await httpClient.post(`payout/reject_payout/${id}`, undefined)
      return response.data
    },
    async setSelfCoolingOff(value: number) {
      const result = await httpClient.post('user/cooling_off', { days: value })
      return result.data
    },
    async setSelfExclusion(value: number) {
      const result = await httpClient.post('user/self_exclusion', { days: value })
      return result.data
    },
    async setTradeLink(link: string) {
      return await httpClient.post(
        'user/set_tradelink',
        { link },
        {
          ignore(response) {
            return (
              response.data?.error_message === 'try_later' ||
              response.data?.error_message === 'save_trade_badurl' ||
              response.data?.error_message === 'internal_error' ||
              response.data?.error_message === 'payment.items.trade_link_error' ||
              response.data?.error_message === 'payment.items.user_two_fa_disabled'
            )
          },
        },
      )
    },
    async setTwoFactorAuth(code: string) {
      return await httpClient.post('user/verify_2fa_code', { code })
    },
    async setUserBanner(id: null | number) {
      const result = await httpClient.post('profile_media/set_banner', { media_id: id })
      return result.data
    },
    async setUserVipAvatar(id: null | number) {
      const result = await httpClient.post('profile_media/set_avatar', { media_id: id })
      return result.data
    },
    async setUserVisit(payload: { landingUrl: string; referrerUrl: string; visitedAt: string }) {
      const form = new FormData()
      form.append('landing_url', payload.landingUrl)
      form.append('referrer_url', payload.referrerUrl)
      form.append('visited_at', payload.visitedAt)
      await httpClient.post('marketing/visit', form, {
        ignore: () => true,
      })
    },
    async submitQuiz(data: { quiz_result: number }) {
      const result = await httpClient.post('quiz/send_result', data)
      return result.data
    },
    async subscribeOnNews(data: { email: string; resend: boolean }) {
      const result = await httpClient.post('subscription/email/subscribe', data)
      return result.data
    },
    async trustpilotReviewAction(payload: { action: string }) {
      const result = await httpClient.post('trustpilot-review/action', payload)
      return result.data
    },
    async updateReplace(payload: { current_item_ids: number[]; inventory_id: number; offset: number }) {
      const result = await httpClient.post('inventory/update_replace', payload)
      return result.data
    },
    async updateUserClientSeed(value: string) {
      const result = await httpClient.post('user/update_client_seed', {
        user_seed: value,
      })
      return result.data
    },
    async userUpdatePartnerCode(code: string) {
      const result = await httpClient.post('partner/update_code', { code })
      return result.data
    },
    async validatePromocode(code: string) {
      const result = await httpClient.post('user/validate_promocode/', { code })
      return result.data as UserPromocode
    },
    async vipTransfer(data: FormData | Record<string, boolean | number[] | string>) {
      const result = await httpClient.post('vip_transfer/store', data)
      return result.data
    },
    async vipTransferImage(data: FormData) {
      const result = await httpClient.post('vip_transfer/image', data)
      return result.data
    },
    async withdrawAffiliateCoins() {
      const result = await httpClient.post('partner/claim')
      return result.data
    },
    async withdrawInventoryItem(payload: { inventory_id: number; is_replace?: number; item_id?: number }) {
      const result = await httpClient.post('inventory/withdraw_item', payload)
      return result.data
    },
  }

  async function syncPreferredLocale(): Promise<void> {
    const locale = localeInst.locale.value
    if (locale.length !== 2) {
      return
    }
    try {
      await transport.setLocale(locale)
    } catch (error) {
      console.error(error)
    }
  }

  return {
    ...transport,
    async addEmail(email: string) {
      try {
        const response = await transport.addEmail(email)

        if (response.status === 'success') {
          userStore.setEmail(email)
          userStore.setEmailState('waiting')
          userStore.setUserIsTempEmail(false)
        }
        return response
      } catch (e: unknown) {
        console.error(e)
        if (typeof e === 'object' && e !== null && 'data' in e) {
          const error = e as {
            data: {
              error_message: string
            }
          }
          return {
            email_time_left: 0,
            error_message: error.data.error_message,
            message: '',
            status: 'error' as const,
          }
        }
      }
    },
    async addPhone(phone: string) {
      try {
        const response = await transport.addPhone(phone)

        if (response.status === 'success') {
          userStore.setPhone(phone)
          userStore.setPhoneState('waiting')
        }
        return response
      } catch (e: unknown) {
        console.error(e)
        if (typeof e === 'object' && e !== null && 'data' in e) {
          const error = e as {
            data: {
              error_message: string
            }
          }
          const errorMessage = error.data.error_message

          if (errorMessage === 'phone_already_used') {
            notify.create({
              description: errorMessage,
              duration: 6000,
              title: 'error',
              type: 'error',
            })
          }
        }
      }
    },
    async fastRegister() {
      const result = await transport.fastRegister()
      if (result.status === 'success' && import.meta.client) {
        window.location.reload()
      }
      return result
    },
    getActiveLocked: createGet()('user/active_locked/info'),
    async getProvablyFair() {
      const response = await transport.getProvablyFair()
      userStore.setProvablyFair(response)
      return response
    },
    async getProvablyFairSeedPairs() {
      const response = await transport.getProvablyFairSeedPairs()
      userStore.setProvablyFairSeedPairs(response)
      return response
    },
    async login(data: {
      login: string
      password: string
      powSolution?: number
      socialConfirmToken?: null | string
    }) {
      const socialConfirmToken = storageService.get('social-confirm-token')
      if (socialConfirmToken) {
        try {
          data.socialConfirmToken = JSON.parse(socialConfirmToken)
        } catch {
          data.socialConfirmToken = null
        }
      }

      const result = await transport.login(data)
      if (result.status === 'error') {
        return false
      }
      await router.replace({ path: route.path, query: { ...route.query, [Query.Auth]: undefined } })
      if (import.meta.client) {
        if (sessionStorage.getItem('AuthRedirect')) {
          const fullPath = sessionStorage.getItem('AuthRedirect') || ''
          sessionStorage.removeItem('AuthRedirect')
          await navigateTo(localeInst.localePath(fullPath), { external: true })
        } else {
          window.location.reload()
        }
      }
      return true
    },
    async loginTelegram() {
      try {
        const telegramUriHash = sessionStorage.getItem('telegram-uri-hash') || ''
        await transport.loginTelegram({
          uri_hash: telegramUriHash,
        })
      } finally {
        await root.init()
      }
    },
    async loginWithPhone(data: { login: string; password: string; socialConfirmToken?: null | string }) {
      const socialConfirmToken = storageService.get('social-confirm-token')
      if (socialConfirmToken) {
        try {
          data.socialConfirmToken = JSON.parse(socialConfirmToken)
        } catch {
          data.socialConfirmToken = null
        }
      }

      const result = await transport.loginWithPhone(data)
      if (result.status === 'error') {
        return false
      }
      await router.replace({ path: route.path, query: { ...route.query, [Query.Auth]: undefined } })
      if (import.meta.client) {
        window.location.reload()
      }
      return true
    },
    async logout() {
      await transport.logout()
      userStore.setUser(null)
      if (localeInst.getRealName(route) !== 'sport-sport') {
        await router.push({ path: localeInst.localePath('/') })
      }
      if (import.meta.client) {
        window.location.reload()
      }
      await root.init()
    },
    async register(data: { login: string; password: string }) {
      const result = await transport.register(data)

      if (result.status === 'error') {
        if (typeof result.redirect_to === 'string') {
          window.location.replace(result.redirect_to)
        }
        return false
      }
      await syncPreferredLocale()
      if (import.meta.client) {
        window.location.replace(localeInst.localePath('/wallet/deposit'))
      }
      return true
    },
    async registerWithPhone(data: { email: string; login: string; password: string }) {
      const result = await transport.registerWithPhone(data)

      if (result.status === 'error') {
        if (typeof result.redirect_to === 'string') {
          window.location.replace(result.redirect_to)
        }
        return result
      }
      await syncPreferredLocale()
      if (import.meta.client) {
        window.location.replace(localeInst.localePath('/wallet/deposit'))
      }
      return result
    },
    async sellInventorySkins(data: { ids?: number[]; isAll?: boolean }) {
      const result = await transport.sellInventorySkins(data)
      if (result.status === 'success') {
        await root.init()
        notify.create({
          duration: 4000,
          title: 'is_sold',
          type: 'success',
        })
      }
      return result
    },
    async sendMarketingTrigger(trigger: string) {
      if (!userStore.isAuth) {
        return
      }
      await transport.sendMarketingTrigger(trigger)
    },
    async sendVerification(code: string) {
      const response = await transport.sendVerification(code)
      if (response.status === 200) {
        if (import.meta.client) {
          window.location.reload()
        }
        await root.init()
        router.push('/')
      }
      return response
    },
    async setCurrency(currency: NestedPick<User, ['user_currency']>) {
      await transport.setCurrentCurrencyByCode(currency.code)
      userStore.setCurrentCurrency(currency)
    },
    async setProfileVisibilityStatus(status: string) {
      const result = await transport.setProfileVisibilityStatus(status)
      if (result.status === 200) {
        userStore.setProfileVisibilityStatus(status)
      }
      return result
    },
    async setSelfCoolingOff(value: number) {
      const response = await transport.setSelfCoolingOff(value)
      await root.init()
      return response
    },
    async setSelfExclusion(value: number) {
      const response = await transport.setSelfExclusion(value)
      await root.init()
      return response
    },
    async setTradeLink(link: string) {
      const result = await transport.setTradeLink(link)
      if (result.status === 200) {
        userStore.setTradeLink(link)
      }
      return result
    },
    async setTwoFactorAuth(code: string) {
      const result = await transport.setTwoFactorAuth(code)
      if (result.data.status === 'success') {
        userStore.setTFA({
          ...userStore.user?.second_factor,
          enabled: true,
        })
      }
      return result
    },
    async setUserBanner(id: null | number, url: null | string) {
      const response = await transport.setUserBanner(id)
      if (response.status === 'success') {
        userStore.setBanner(url)
        return true
      }
      return false
    },
    async setUserProfileInfo(userId: number) {
      const response = await transport.getUserProfileInfo(userId)
      if (response.status === 'success') {
        userStore.setUserProfileInfo(response)
      }
      return response
    },
    async setUserVipAvatar(id: null | number, url: null | string) {
      const response = await transport.setUserVipAvatar(id)
      if (response.status === 'success') {
        userStore.setAvatar(url)
        return true
      }
      return false
    },
    async setUserVisit() {
      const landingUrl = window.location.href
      const referrerUrl = isTelegram() ? 'https://tg.com/' : document.referrer
      const visitedAt = Date.now().valueOf().toString()
      if (!isEmpty(referrerUrl)) {
        await transport.setUserVisit({
          landingUrl,
          referrerUrl,
          visitedAt,
        })
      }
    },
    async softCancelDeposit(depositId: number) {
      const result = await httpClient.post('user/transactions/deposit/cancel', { payment_id: depositId })
      return {
        data: result.data,
        status: result.data.status,
      }
    },
  }
})

export type UserCasePort = ReturnType<typeof useUser>
export type UserPort = ReturnType<typeof useUser>

export default useUser
